import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  Loader2,
  MapPin,
  Package,
  RefreshCw,
  Search,
  ShoppingBag,
} from 'lucide-react'
import { fetchStores, type StoreOption } from '../api/auth'
import { fetchProductOverview, type ProductRow } from '../api/products'
import { DashboardGroupSubMenu } from '../components/DashboardGroupSubMenu'
import { PeriodControls } from '../components/PeriodControls'
import { ShopPicker } from '../components/ShopPicker'
import { useAuth } from '../context/AuthContext'
import {
  insightShopsForUser,
  shopLabel,
} from '../dashboard/insightShops'
import { canSelectShops } from '../lib/access'
import { formatCurrency, formatNumber, formatShortTimestamp } from '../lib/format'
import { rangePresets, type DateRange } from '../lib/ranges'

const BUTTON_BASE =
  'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors'
const BUTTON_IDLE =
  'border-slate-200 text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200'

function KpiCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint: string
}) {
  return (
    <article className="card p-3">
      <span className="kpi-label">{label}</span>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-tight text-slate-900 dark:text-white">
        {value}
      </p>
      <p className="mt-1 text-[11px] leading-snug text-slate-500 dark:text-slate-400">{hint}</p>
    </article>
  )
}

function variantSummary(row: ProductRow): string {
  if (row.variants.length === 0) return '—'
  const names = row.variants
    .slice(0, 3)
    .map((variant) => `${variant.name} (${formatNumber(variant.count, 0)})`)
    .join(' · ')
  const extra = row.variants.length - 3
  return extra > 0 ? `${names} +${extra}` : names
}

function isDisposable(row: ProductRow): boolean {
  return (row.category ?? '').trim().toLowerCase() === 'disposables'
}

function ProductTable({
  rows,
  emptyLabel,
  showModifiers,
}: {
  rows: ProductRow[]
  emptyLabel: string
  showModifiers: boolean
}) {
  if (rows.length === 0) {
    return (
      <p className="px-3 py-8 text-center text-xs text-slate-500 dark:text-slate-400">
        {emptyLabel}
      </p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-xs md:min-w-[820px]">
        <thead>
          <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400 dark:border-white/10 dark:text-slate-500">
            <th className="px-3 py-2 font-semibold">Product</th>
            <th className="hidden px-3 py-2 font-semibold md:table-cell">Category</th>
            <th className="px-3 py-2 text-right font-semibold">Units</th>
            <th className="px-3 py-2 text-right font-semibold">Revenue</th>
            <th className="px-3 py-2 font-semibold">Share</th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">Orders</th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">Avg price</th>
            {showModifiers && <th className="hidden px-3 py-2 font-semibold lg:table-cell">Top modifiers</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.kind}-${row.product_id ?? row.product_fee_id ?? row.name}`}
              className="border-b border-slate-100 align-top last:border-0 dark:border-white/5"
            >
              <td className="px-3 py-2.5">
                <p className="font-semibold text-slate-800 dark:text-slate-100">{row.name}</p>
                <p className="mt-0.5 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                  {row.external_id
                    ? `ext ${row.external_id}`
                    : row.product_id
                      ? `product ${row.product_id}`
                      : row.product_fee_id
                        ? `fee ${row.product_fee_id}`
                        : 'unmapped'}
                </p>
              </td>
              <td className="hidden px-3 py-2.5 text-slate-600 md:table-cell dark:text-slate-300">
                {row.kind === 'fee' ? 'Fee' : (row.category ?? 'Uncategorised')}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 dark:text-slate-200">
                {formatNumber(row.quantity, 1)}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums">
                <span className="font-semibold text-slate-900 dark:text-white">
                  {formatCurrency(row.revenue_eur)}
                </span>
                {row.discount_eur > 0 && (
                  <span className="ml-1 text-[10px] text-slate-400 dark:text-slate-500">
                    −{formatCurrency(row.discount_eur)}
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                    <span
                      className="block h-full rounded-full bg-salsa-500"
                      style={{ width: `${Math.min(row.share_pct, 100)}%` }}
                    />
                  </span>
                  <span className="w-10 text-right tabular-nums text-[10px] text-slate-500 dark:text-slate-400">
                    {row.share_pct.toFixed(1)}%
                  </span>
                </div>
              </td>
              <td className="hidden px-3 py-2.5 text-right tabular-nums text-slate-700 md:table-cell dark:text-slate-200">
                {formatNumber(row.orders, 0)}
              </td>
              <td className="hidden px-3 py-2.5 text-right tabular-nums text-slate-700 md:table-cell dark:text-slate-200">
                {formatCurrency(row.average_price_eur)}
              </td>
              {showModifiers && (
                <td className="hidden max-w-[260px] px-3 py-2.5 text-[11px] leading-snug text-slate-500 lg:table-cell dark:text-slate-400">
                  <span title={row.variants.map((variant) => variant.name).join(', ')}>
                    {variantSummary(row)}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Product Overview — product and fee mix from paid Butlaroo order lines.
 *
 * Opens with no shop selected for a multi-shop account; a shop must be chosen
 * explicitly. An account with exactly one shop opens on it automatically because
 * there is nothing to choose (D-101). The endpoint returns line revenue
 * (`unitPrice × quantity`), line discounts, modifiers and a separate fee list.
 * This page deliberately shows the source and reconciliation instead of
 * pretending the vendor is a margin system: recipe cost and refunds are not
 * available yet.
 */
export function ProductOverviewPage() {
  const { user } = useAuth()
  const now = useMemo(() => new Date(), [])
  const presets = useMemo(
    () => rangePresets(now).filter((preset) => preset.label !== 'All time'),
    [now],
  )
  const [range, setRange] = useState<DateRange>(() => {
    const week = rangePresets(new Date()).find((preset) => preset.label === 'This week')
    return week
      ? { from: week.from, to: week.to, preset: 'This week' }
      : { from: presets[0].from, to: presets[0].to }
  })
  const [selectedShopId, setSelectedShopId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [showDisposables, setShowDisposables] = useState(false)
  const autoSelectedRef = useRef(false)

  const storesQuery = useQuery({
    queryKey: ['stores'],
    queryFn: fetchStores,
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  })
  const catalogue: StoreOption[] = storesQuery.data ?? []
  const shops = useMemo(() => insightShopsForUser(user, catalogue), [user, catalogue])
  const canPick = canSelectShops(user)

  // A one-shop account has nothing to choose, so open on its only shop instead
  // of asking for a pointless click (D-101). Multi-shop accounts keep the
  // explicit no-selection default, and a later choice is not overridden.
  useEffect(() => {
    if (autoSelectedRef.current || shops.length !== 1) return
    autoSelectedRef.current = true
    setSelectedShopId(shops[0].id)
  }, [shops])

  useEffect(() => {
    if (selectedShopId !== null && !shops.some((shop) => shop.id === selectedShopId)) {
      setSelectedShopId(null)
    }
  }, [shops, selectedShopId])

  const selectedShop = shops.find((shop) => shop.id === selectedShopId) ?? null
  const productQuery = useQuery({
    queryKey: ['butlaroo-product-overview', selectedShop?.id, range.from, range.to],
    queryFn: ({ signal }) =>
      fetchProductOverview({
        from: range.from,
        to: range.to,
        storeIds: selectedShop ? [selectedShop.id] : undefined,
        signal,
      }),
    enabled: selectedShop !== null,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  })

  const data = productQuery.data
  const productError =
    productQuery.error instanceof Error
      ? productQuery.error.message
      : 'The product breakdown could not be loaded.'
  const isInitialLoading = Boolean(selectedShop) && productQuery.isPending
  const isRefreshing = productQuery.isFetching && !productQuery.isPending

  const allProducts = data?.products ?? []
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          allProducts
            .map((row) => row.category)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    [allProducts],
  )
  const disposableCount = allProducts.filter(isDisposable).length
  const visibleRows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase()
    return allProducts.filter((row) => {
      if (!showDisposables && isDisposable(row)) return false
      if (category && row.category !== category) return false
      if (!needle) return true
      return `${row.name} ${row.category ?? ''} ${row.external_id ?? ''}`
        .toLocaleLowerCase()
        .includes(needle)
    })
  }, [allProducts, category, search, showDisposables])

  useEffect(() => {
    if (category && !categories.includes(category)) setCategory('')
  }, [categories, category])

  const unavailableStores = data?.stores.filter((store) => !store.available) ?? []
  const warnings = data?.warnings ?? []
  const showEmpty =
    Boolean(data) &&
    !isInitialLoading &&
    !productQuery.isError &&
    allProducts.length === 0 &&
    (data?.fees.length ?? 0) === 0

  return (
    <div id="product-overview-panel" className="space-y-3">
      <DashboardGroupSubMenu
        activeKey="product_analysis"
        fallbackGroupKey="insights"
        panelId="product-overview-panel"
      />

      <section className="card space-y-3 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-700 text-white shadow-sm"
              aria-hidden="true"
            >
              <Package className="h-4 w-4" />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                Product Overview
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Product, fee and modifier mix from paid Butlaroo order lines.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {data && data.totals.refunds_eur > 0 && (
              <span className="badge bg-rose-500/10 text-rose-700 dark:text-rose-300">
                Refunds {formatCurrency(data.totals.refunds_eur)}
              </span>
            )}
            {isRefreshing && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                Refreshing
              </span>
            )}
            <button
              type="button"
              onClick={() => void productQuery.refetch()}
              disabled={!selectedShop || productQuery.isFetching}
              className={`${BUTTON_BASE} ${BUTTON_IDLE} inline-flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${productQuery.isFetching ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              Refresh
            </button>
          </div>
        </div>

        <PeriodControls value={range} onChange={setRange} presets={presets} now={now} />

        {shops.length === 0 ? (
          <p className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <MapPin className="h-3 w-3" aria-hidden="true" />
            No shops in your scope.
          </p>
        ) : canPick ? (
          <ShopPicker
            stores={shops}
            selectedId={selectedShopId}
            onSelect={setSelectedShopId}
            hint={
              selectedShop
                ? shopLabel(selectedShop)
                : 'No shop selected · choose one shop for this overview'
            }
          />
        ) : selectedShop ? (
          <p className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <MapPin className="h-3 w-3" aria-hidden="true" />
            {shopLabel(selectedShop)}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => setSelectedShopId(shops[0].id)}
            className={`${BUTTON_BASE} ${BUTTON_IDLE} inline-flex items-center gap-1`}
          >
            <MapPin className="h-3 w-3" aria-hidden="true" />
            Use {shopLabel(shops[0])}
          </button>
        )}

        {storesQuery.isError && (
          <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Could not load the shop list.
            <button
              type="button"
              onClick={() => void storesQuery.refetch()}
              className="font-semibold underline underline-offset-2"
            >
              Retry
            </button>
          </p>
        )}
      </section>

      {!selectedShop ? (
        <section className="card flex items-center gap-2 p-4 text-sm text-slate-500 dark:text-slate-400">
          <ShoppingBag className="h-4 w-4" aria-hidden="true" />
          <span>Choose a shop to build the product overview.</span>
        </section>
      ) : (
        <>
          {productQuery.isError && (
            <div className="flex flex-wrap items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <strong>Product data is unavailable.</strong> {productError}
                {data ? ' Showing the last successful response.' : ''}
              </span>
              <button
                type="button"
                onClick={() => void productQuery.refetch()}
                disabled={productQuery.isFetching}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${productQuery.isFetching ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                Retry
              </button>
            </div>
          )}

          {data &&
            (unavailableStores.length > 0 ||
              warnings.length > 0 ||
              data.truncated) && (
              <div className="flex items-start gap-2 rounded-2xl border border-amber-200/80 bg-amber-50/60 px-4 py-3 text-[11px] leading-relaxed text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <div className="space-y-1">
                  {unavailableStores.map((store) => (
                    <p key={`unavailable-${store.store_id}`}>
                      <strong>#{store.store_code}</strong> is unavailable
                      {store.error_message ? `: ${store.error_message}` : '.'}
                    </p>
                  ))}
                  {data.truncated && (
                    <p>
                      Vendor paging was truncated, so these product totals may be incomplete.
                      Refresh or narrow the period.
                    </p>
                  )}
                  {warnings.map((warning, index) => (
                    <p key={`${warning}-${index}`}>{warning}</p>
                  ))}
                </div>
              </div>
            )}

          {isInitialLoading ? (
            <section className="card flex items-center gap-2 p-4 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading paid order lines for {shopLabel(selectedShop)}…
            </section>
          ) : showEmpty ? (
            <section className="card flex items-center gap-2 p-4 text-sm text-slate-500 dark:text-slate-400">
              <ShoppingBag className="h-4 w-4" aria-hidden="true" />
              No product or fee lines were found for this period.
            </section>
          ) : data ? (
            <>
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <KpiCard
                  label="Total sales"
                  value={formatCurrency(data.totals.line_total_eur)}
                  hint="Products + fees · VAT-exclusive · matches Insights and Sales Analysis"
                />
                <KpiCard
                  label="Product revenue"
                  value={formatCurrency(data.totals.product_revenue_eur)}
                  hint={`Fees ${formatCurrency(data.totals.fee_revenue_eur)} · discounts ${formatCurrency(data.totals.discount_eur)}`}
                />
                <KpiCard
                  label="Paid orders"
                  value={formatNumber(data.totals.orders, 0)}
                  hint={`${formatNumber(data.order_lines, 0)} order lines · ${formatNumber(data.variant_rows, 0)} modifiers`}
                />
                <KpiCard
                  label="Units sold"
                  value={formatNumber(data.totals.quantity, 1)}
                  hint={`${formatNumber(data.totals.products, 0)} products · ${formatNumber(data.totals.fees, 0)} fee lines`}
                />
              </div>

              <section className="card overflow-hidden">
                <header className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-white/10">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      Products
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      {formatNumber(allProducts.length, 0)} selling products in this period,
                      sorted by revenue.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="relative">
                      <Search
                        className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                        aria-hidden="true"
                      />
                      <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search product or category"
                        className="w-52 rounded-lg border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-[11px] text-slate-700 outline-none focus:border-salsa-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                      />
                    </label>
                    <select
                      value={category}
                      onChange={(event) => {
                        const next = event.target.value
                        setCategory(next)
                        if (next.toLowerCase() === 'disposables') setShowDisposables(true)
                      }}
                      aria-label="Filter by category"
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700 outline-none focus:border-salsa-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                    >
                      <option value="">All categories</option>
                      {categories.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                    {disposableCount > 0 && (
                      <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                        <input
                          type="checkbox"
                          checked={showDisposables}
                          onChange={(event) => setShowDisposables(event.target.checked)}
                          className="h-3.5 w-3.5 accent-salsa-500"
                        />
                        Show disposables ({formatNumber(disposableCount, 0)})
                      </label>
                    )}
                  </div>
                </header>
                <ProductTable
                  rows={visibleRows}
                  emptyLabel={
                    allProducts.length === 0
                      ? 'No products were sold in this period.'
                      : 'No products match these filters.'
                  }
                  showModifiers
                />
              </section>

              <section className="card overflow-hidden">
                <header className="border-b border-slate-200 px-4 py-3 dark:border-white/10">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                    Fees &amp; deposits
                  </h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    ProductFee lines are shown separately so packaging does not look like a menu
                    product.
                  </p>
                </header>
                <ProductTable
                  rows={data.fees}
                  emptyLabel="No fee lines in this period."
                  showModifiers={false}
                />
              </section>

              <p className="px-1 text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
                Source: paid Butlaroo orders → order lines + modifiers → products/categories.
                {` ${formatNumber(data.order_rows, 0)} paid orders · `}
                {`${formatNumber(data.order_lines, 0)} order lines · `}
                {`${formatNumber(data.variant_rows, 0)} modifier rows · `}
                {`${formatNumber(data.pages_fetched, 0)} vendor pages · `}
                fetched {formatShortTimestamp(data.fetched_at, data.timezone)}. Recipe margin is
                not part of this view.
              </p>
            </>
          ) : null}
        </>
      )}
    </div>
  )
}

export default ProductOverviewPage
